import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  Search, Plus, LayoutGrid, List, 
  Edit3, Trash2, Eye, Filter, Package as PackageIcon,
  Thermometer, Box, ChevronRight
} from 'lucide-react';
import { API_BASE_LOCAL_URL } from '../../../config';
import ImageCarousel from "./ImageCarousel";

const API_BASE = `${API_BASE_LOCAL_URL}/api/products`;

// ---------------- ProductCard (Refined Grid Item) ----------------
function ProductCard({ product, onEdit, onDelete, onView }) {
  const { productCode, productName, productCategory, drying, excipients } = product;

  return (
    <div className="col-xl-3 col-lg-4 col-md-6 mb-3">
      <div 
        className="card h-100 border border-light shadow-xs hover-shadow-md transition-all rounded-3 overflow-hidden"
        style={{ cursor: 'pointer', border: '1px solid #f0f0f0' }}
        onClick={() => onView(product)}
      >
        {/* Banner */}
        <div className="position-relative overflow-hidden" style={{ height: "140px" }}>
          <ImageCarousel
            images={product.bannerImages || []}
            height={140}
          />
          <div 
            className="position-absolute top-0 end-0 m-2 badge bg-dark bg-opacity-75 backdrop-blur font-monospace"
            style={{ fontSize: '0.7rem', fontWeight: '600' }}
          >
            {productCode}
          </div>
        </div>

        {/* Body */}
        <div className="card-body p-3">
          <div className="mb-2">
            <span 
              className="text-uppercase fw-bold text-success" 
              style={{ letterSpacing: '0.05em', fontSize: '0.6rem', opacity: 0.8 }}
            >
               {productCategory || "General Catalog"}
            </span>
            <h6 
              className="fw-bold text-truncate mb-0 mt-1" 
              style={{ fontSize: '0.9rem', color: '#2d3436' }} 
              title={productName}
            >
              {productName}
            </h6>
          </div>

          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', color: '#636e72' }}>
              <Thermometer size={12} className="text-muted" />
              <span className="text-truncate">Drying: <span className="text-dark fw-medium">{drying || "N/A"}</span></span>
            </div>
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.75rem', color: '#636e72' }}>
              <Box size={12} className="text-muted" />
              <span className="text-truncate">Excipients: <span className="text-dark fw-medium">{excipients || "None"}</span></span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="card-footer bg-light bg-opacity-50 border-top-0 p-2 d-flex gap-2">
          <button 
            className="btn btn-white border btn-sm flex-grow-1 py-1 rounded-2 d-flex align-items-center justify-content-center gap-1 shadow-xs"
            style={{ fontSize: '0.75rem', fontWeight: '600' }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(productCode);
            }}
          >
            <Edit3 size={12} className="text-primary" /> Edit
          </button>
          <button 
            className="btn btn-white border btn-sm rounded-2 px-2 shadow-xs"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(productCode);
            }}
          >
            <Trash2 size={12} className="text-danger" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Main Component ----------------
export default function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const axiosAuth = useMemo(() => axios.create({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    setLoading(true);
    axiosAuth.get(API_BASE)
      .then((res) => setProducts(res.data))
      .catch((err) => {
        if (err.response?.status === 401) navigate("/login");
        console.error("Fetch error:", err);
      })
      .finally(() => setLoading(false));
  }, [navigate, axiosAuth]);

  const filteredProducts = useMemo(() => 
    products.filter((p) => 
      p.productName?.toLowerCase().includes(search.toLowerCase()) ||
      p.productCode?.toLowerCase().includes(search.toLowerCase())
    ), [products, search]
  );

  const handleDelete = useCallback((productCode) => {
    if (!window.confirm(`Delete product ${productCode}?`)) return;
    axiosAuth.delete(`${API_BASE}/${productCode}`)
      .then(() => setProducts((prev) => prev.filter((p) => p.productCode !== productCode)))
      .catch(() => alert("Delete failed. Check permissions."));
  }, [axiosAuth]);

  return (
    <div className="container-fluid py-3 px-lg-5 animate-fade-in" style={{ backgroundColor: '#fdfdfd' }}>
      
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold text-dark mb-0" style={{ letterSpacing: '-0.02em' }}>Products</h4>
        </div>
        <button 
          className="btn btn-success shadow-sm px-3 py-2 d-flex align-items-center gap-2 rounded-2"
          style={{ fontSize: '0.85rem', fontWeight: '600' }}
          onClick={() => navigate("/AdminDashboard/products/add")}
        >
          <Plus size={16} strokeWidth={3} /> Add Product
        </button>
      </div>

      {/* Toolbar */}
      <div className="d-flex flex-column flex-md-row gap-3 mb-4 align-items-center">
        <div className="position-relative flex-grow-1 w-100">
          <Search size={16} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
          <input
            type="text"
            className="form-control ps-5 border shadow-xs py-2 rounded-2"
            style={{ fontSize: '0.85rem', backgroundColor: '#fff' }}
            placeholder="Search by name, SKU or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        
        <div className="d-flex gap-4 w-100 w-md-auto">
          <div className="btn-group border rounded-2 shadow-xs bg-white p-1">
            <button 
              className={`btn btn-sm border-0 px-3 py-1 rounded-1 ${viewMode === 'grid' ? 'bg-primary text-white shadow-sm' : 'text-muted'}`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={14} />
            </button>
            <button 
              className={`btn btn-sm border-0 px-3 py-1 rounded-1 ${viewMode === 'list' ? 'bg-primary text-white shadow-sm' : 'text-muted'}`}
              onClick={() => setViewMode('list')}
            >
              <List size={14} />
            </button>
          </div>
          <button className="btn btn-white border shadow-xs text-muted d-flex align-items-center gap-2 px-3 py-2 rounded-2 small fw-medium" style={{ fontSize: '0.8rem' }}>
            <Filter size={14} /> Filter
          </button>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="text-center py-5 mt-5">
          <div className="spinner-border spinner-border-sm text-success" role="status"></div>
          <p className="text-muted mt-3 small fw-medium">Loading inventory...</p>
        </div>
      ) : filteredProducts.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="row g-3">
            {filteredProducts.map((p) => (
              <ProductCard
                key={p.productCode}
                product={p}
                onEdit={(code) => navigate(`/AdminDashboard/products/${code}/edit`)}
                onDelete={handleDelete}
                onView={(p) => navigate(`/AdminDashboard/products/${p.productCode}`)}
              />
            ))}
          </div>
        ) : (
          <div className="card border shadow-xs rounded-3 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                <thead className="bg-light">
                  <tr className="small text-uppercase fw-bold text-muted" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                    <th className="px-4 py-3 border-0 text-center" width="80">Preview</th>
                    <th className="py-3 border-0">Product Details</th>
                    <th className="py-3 border-0">Product Code</th>
                    <th className="py-3 border-0">Category</th>
                    <th className="py-3 border-0">Form</th>
                    <th className="py-3 border-0">Extraction</th>
                    <th className="py-3 border-0">Origin</th>
                    <th className="py-3 border-0">Ingredients</th>
                    <th className="py-3 border-0">Process</th>
                    <th className="py-3 border-0 text-end px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {filteredProducts.map((p) => (
                    <tr 
                      key={p.productCode} 
                      className="cursor-pointer"
                      onClick={() => navigate(`/AdminDashboard/products/${p.productCode}`)}
                    >
                      <td className="px-4 py-2 text-center">
                        <img 
                          src={p.bannerImages?.[0] || 'https://via.placeholder.com/40'} 
                          className="rounded-2 object-fit-cover shadow-xs border" 
                          style={{ width: '40px', height: '40px' }}
                          alt="" 
                        />
                      </td>
                      <td>
                        <div className="fw-bold text-dark">{p.productName}</div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{p.drying || 'Process N/A'}</div>
                      </td>
                      <td><code className="text-primary fw-bold bg-primary-subtle px-2 py-1 rounded small">{p.productCode}</code></td>
                      <td><span className="badge bg-light text-dark border fw-medium">{p.productCategory || "General"}</span></td>

                      <td>{p.form || "-"}</td>
                      <td>{p.extractionType || "-"}</td>
                      <td>{p.origin || "-"}</td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {p.ingredients?.length || 0}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {p.processes?.length || 0}
                        </span>
                      </td>
                     
                      <td className="text-end px-4">
                         <div className="d-flex justify-content-end gap-2" onClick={e => e.stopPropagation()}>
                            <button className="btn btn-outline-primary btn-sm rounded-2 p-1" onClick={() => navigate(`/AdminDashboard/products/${p.productCode}/edit`)}>
                              <Edit3 size={14} />
                            </button>
                            <button className="btn btn-outline-danger btn-sm rounded-2 p-1" onClick={() => handleDelete(p.productCode)}>
                              <Trash2 size={14} />
                            </button>
                         </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-5 border rounded-4 bg-white shadow-xs">
          <PackageIcon size={40} className="text-muted mb-3 opacity-50" />
          <h6 className="fw-bold">No results found</h6>
          <p className="text-muted small">Try refining your search keywords.</p>
        </div>
      )}

      <style>{`
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .hover-shadow-md { transition: all 0.2s ease; }
        .hover-shadow-md:hover { transform: translateY(-3px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
        .btn-white { background: #fff; color: #444; }
        .btn-white:hover { background: #f8f9fa; }
        .backdrop-blur { backdrop-filter: blur(4px); }
        .font-monospace { font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
      `}</style>
    </div>
  );
}